import React, { useState } from 'react';
import { XIcon, CheckIcon, ChevronRightIcon, ChevronLeftIcon, ShieldCheckIcon, AlertTriangleIcon, PlusIcon, TrashIcon } from 'lucide-react';
interface CreatePolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const STEPS = [{
  id: 1,
  title: 'Policy Details'
}, {
  id: 2,
  title: 'Rules'
}, {
  id: 3,
  title: 'Enforcement'
}, {
  id: 4,
  title: 'Review'
}];
export function CreatePolicyModal({
  isOpen,
  onClose
}: CreatePolicyModalProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    category: 'Brand Identity',
    description: '',
    rules: [{
      id: 1,
      condition: '',
      value: ''
    }],
    enforcement: 'Warning'
  });
  if (!isOpen) return null;
  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  const addRule = () => {
    setFormData({
      ...formData,
      rules: [...formData.rules, {
        id: Date.now(),
        condition: '',
        value: ''
      }]
    });
  };
  const removeRule = (id: number) => {
    setFormData({
      ...formData,
      rules: formData.rules.filter(r => r.id !== id)
    });
  };
  return <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose} data-id="element-544">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()} data-id="element-545">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50" data-id="element-546">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-547">
            Create Compliance Policy
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500" data-id="element-548">
            <XIcon size={20} data-id="element-549" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-8 py-6" data-id="element-550">
          <div className="flex items-center justify-between relative" data-id="element-551">
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-full h-0.5 bg-gray-200 -z-10" data-id="element-552"></div>
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 h-0.5 bg-blue-600 -z-10 transition-all duration-300" style={{
            width: `${(currentStep - 1) / (STEPS.length - 1) * 100}%`
          }} data-id="element-553"></div>

            {STEPS.map(step => {
            const isCompleted = step.id < currentStep;
            const isActive = step.id === currentStep;
            return <div key={step.id} className="flex flex-col items-center bg-white px-2" data-id="element-554">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors duration-300 ${isCompleted ? 'bg-green-500 text-white' : isActive ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-555">
                    {isCompleted ? <CheckIcon size={16} data-id="element-556" /> : step.id}
                  </div>
                  <span className={`text-xs mt-2 font-medium ${isActive ? 'text-blue-600' : 'text-gray-500'}`} data-id="element-557">
                    {step.title}
                  </span>
                </div>;
          })}
          </div>
        </div>

        {/* Content */}
        <div className="px-8 py-4 min-h-[320px]" data-id="element-558">
          {currentStep === 1 && <div className="space-y-4 animate-in slide-in-from-right-4 duration-300" data-id="element-559">
              <div data-id="element-560">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-561">
                  Policy Name
                </label>
                <input type="text" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" placeholder="e.g. Logo Usage Guidelines" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} data-id="element-562" />
              </div>
              <div data-id="element-563">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-564">
                  Category
                </label>
                <select className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white" value={formData.category} onChange={e => setFormData({
              ...formData,
              category: e.target.value
            })} data-id="element-565">
                  <option data-id="element-566">Brand Identity</option>
                  <option data-id="element-567">Pricing & Offers</option>
                  <option data-id="element-568">Messaging</option>
                  <option data-id="element-569">Creative Quality</option>
                </select>
              </div>
              <div data-id="element-570">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-571">
                  Description
                </label>
                <textarea className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none h-24" placeholder="Explain the purpose of this policy..." value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} data-id="element-572" />
              </div>
            </div>}

          {currentStep === 2 && <div className="space-y-4 animate-in slide-in-from-right-4 duration-300" data-id="element-573">
              <div className="flex justify-between items-center mb-2" data-id="element-574">
                <label className="block text-sm font-medium text-gray-700" data-id="element-575">
                  Policy Rules
                </label>
                <button onClick={addRule} className="text-sm text-blue-600 font-medium hover:text-blue-700 flex items-center gap-1" data-id="element-576">
                  <PlusIcon size={16} data-id="element-577" /> Add Rule
                </button>
              </div>

              <div className="space-y-3 max-h-[250px] overflow-y-auto pr-2" data-id="element-578">
                {formData.rules.map((rule, index) => <div key={rule.id} className="flex gap-3 items-start p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-579">
                    <span className="mt-2.5 text-sm font-medium text-gray-500 w-6" data-id="element-580">
                      {index + 1}.
                    </span>
                    <div className="flex-1 space-y-2" data-id="element-581">
                      <select className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white" data-id="element-582">
                        <option value="" data-id="element-583">Select Condition...</option>
                        <option data-id="element-584">Logo Visibility</option>
                        <option data-id="element-585">Color Match</option>
                        <option data-id="element-586">Price Discrepancy</option>
                        <option data-id="element-587">Text Content</option>
                      </select>
                      <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Value / Criteria" data-id="element-588" />
                    </div>
                    <button onClick={() => removeRule(rule.id)} className="mt-2 text-gray-400 hover:text-red-500 transition-colors" data-id="element-589">
                      <TrashIcon size={18} data-id="element-590" />
                    </button>
                  </div>)}
              </div>
            </div>}

          {currentStep === 3 && <div className="space-y-6 animate-in slide-in-from-right-4 duration-300" data-id="element-591">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-592">
                Enforcement Action
              </label>
              <div className="grid grid-cols-1 gap-3" data-id="element-593">
                <label className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all ${formData.enforcement === 'Warning' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-594">
                  <input type="radio" name="enforcement" className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" checked={formData.enforcement === 'Warning'} onChange={() => setFormData({
                ...formData,
                enforcement: 'Warning'
              })} data-id="element-595" />
                  <div className="ml-4" data-id="element-596">
                    <div className="flex items-center gap-2" data-id="element-597">
                      <AlertTriangleIcon size={18} className="text-amber-500" data-id="element-598" />
                      <span className="font-medium text-gray-900" data-id="element-599">
                        Issue Warning
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-600">
                      Partner receives a notification to correct the violation.
                    </p>
                  </div>
                </label>

                <label className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all ${formData.enforcement === 'Suspension' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-601">
                  <input type="radio" name="enforcement" className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" checked={formData.enforcement === 'Suspension'} onChange={() => setFormData({
                ...formData,
                enforcement: 'Suspension'
              })} data-id="element-602" />
                  <div className="ml-4" data-id="element-603">
                    <div className="flex items-center gap-2" data-id="element-604">
                      <ShieldCheckIcon size={18} className="text-red-500" data-id="element-605" />
                      <span className="font-medium text-gray-900" data-id="element-606">
                        Campaign Suspension
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-607">
                      Automatically pause active campaigns until resolved.
                    </p>
                  </div>
                </label>
              </div>
            </div>}

          {currentStep === 4 && <div className="space-y-6 animate-in slide-in-from-right-4 duration-300" data-id="element-608">
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 space-y-4" data-id="element-609">
                <div className="flex justify-between border-b border-gray-200 pb-3" data-id="element-610">
                  <span className="text-sm text-gray-500" data-id="element-611">Policy Name</span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-612">
                    {formData.name || 'Untitled Policy'}
                  </span>
                </div>
                <div className="flex justify-between border-b border-gray-200 pb-3" data-id="element-613">
                  <span className="text-sm text-gray-500" data-id="element-614">Category</span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-615">
                    {formData.category}
                  </span>
                </div>
                <div className="flex justify-between border-b border-gray-200 pb-3" data-id="element-616">
                  <span className="text-sm text-gray-500" data-id="element-617">Rules Defined</span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-618">
                    {formData.rules.length} Rules
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-619">
                  <span className="text-sm text-gray-500" data-id="element-620">Enforcement</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800" data-id="element-621">
                    {formData.enforcement}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-100" data-id="element-622">
                <div className="p-1 bg-blue-100 rounded-full text-blue-600 mt-0.5" data-id="element-623">
                  <CheckIcon size={14} data-id="element-624" />
                </div>
                <div data-id="element-625">
                  <h4 className="text-sm font-medium text-blue-900" data-id="element-626">
                    Ready to Publish
                  </h4>
                  <p className="text-xs text-blue-700 mt-1" data-id="element-627">
                    This policy will be active immediately. All partners will be
                    notified of the new compliance requirements.
                  </p>
                </div>
              </div>
            </div>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center" data-id="element-628">
          <button onClick={handleBack} className={`flex items-center px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors ${currentStep === 1 ? 'invisible' : ''}`} data-id="element-629">
            <ChevronLeftIcon size={16} className="mr-1" data-id="element-630" />
            Back
          </button>
          <div className="flex gap-3" data-id="element-631">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 rounded-lg transition-colors" data-id="element-632">
              Cancel
            </button>
            <button onClick={handleNext} className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 shadow-sm transition-all transform active:scale-95" data-id="element-633">
              {currentStep === STEPS.length ? 'Publish Policy' : 'Next Step'}
              {currentStep !== STEPS.length && <ChevronRightIcon size={16} className="ml-1" data-id="element-634" />}
            </button>
          </div>
        </div>
      </div>
    </div>;
}